import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AgreementMetaDataConstants } from './agreement-metadata-generation.helper';
import { TextReference } from '../types/text-reference.type';

@Injectable({
    providedIn: 'root'
})
export class MetadataHighlightsService {
    private highlightedFieldsSubject = new BehaviorSubject<Set<string>>(new Set());
    private fieldTextReferencesSubject = new BehaviorSubject<Map<string, TextReference>>(new Map());
    private fieldFocusedSubject = new BehaviorSubject<AgreementMetaDataConstants | null>(null);

    /**
     * Observable of currently highlighted fields with distinctUntilChanged to prevent duplicate emissions
     */
    public highlightedFields$ = this.highlightedFieldsSubject.asObservable().pipe(
        distinctUntilChanged((prev, curr) => {
            // Compare Sets by checking if they have the same values
            if (prev.size !== curr.size) return false;
            for (const item of prev) {
                if (!curr.has(item)) return false;
            }
            return true;
        })
    );

    /**
     * Set multiple fields as highlighted
     */
    public setHighlightedFields(fields: string[]): void {
        this.highlightedFieldsSubject.next(new Set(fields));
    }

    /**
     * Remove a single field from highlights
     */
    public removeHighlightedField(field: string): void {
        const currentFields = this.highlightedFieldsSubject.value;
        if (currentFields.has(field)) {
            const newFields = new Set(currentFields);
            newFields.delete(field);
            this.highlightedFieldsSubject.next(newFields);
        }
    }

    /**
     * Clear all highlighted fields
     */
    public clearHighlights(): void {
        if (this.highlightedFieldsSubject.value.size > 0) {
            this.highlightedFieldsSubject.next(new Set());
        }
    }

    /**
     * Check if a specific field is highlighted (returns Observable)
     */
    public isFieldHighlighted$(fieldName: string): Observable<boolean> {
        return this.highlightedFields$.pipe(map((fields) => fields.has(fieldName)));
    }

    /**
     * Check if a specific enum field is highlighted (returns Observable)
     */
    public isEnumFieldHighlighted$(enumValue: AgreementMetaDataConstants): Observable<boolean> {
        const fieldName = AgreementMetaDataConstants[enumValue];
        return this.isFieldHighlighted$(fieldName);
    }

    /**
     * Synchronous check if field is highlighted (for immediate use)
     */
    public isFieldHighlighted(fieldName: string): boolean {
        return this.highlightedFieldsSubject.value.has(fieldName);
    }

    /**
     * Synchronous check if enum field is highlighted
     */
    public isEnumFieldHighlighted(enumValue: AgreementMetaDataConstants): boolean {
        const fieldName = AgreementMetaDataConstants[enumValue];
        return this.isFieldHighlighted(fieldName);
    }

    /**
     * Set PDF text references for fields
     * Also automatically updates highlighted fields from the Map keys
     * @param references Map of field names to their text references
     */
    public setFieldTextReferences(references: Map<string, TextReference>): void {
        this.fieldTextReferencesSubject.next(references);
        // Automatically update highlighted fields from text references
        this.setHighlightedFields(Array.from(references.keys()));
    }

    /**
     * Add a single text reference for a field
     */
    public addFieldTextReference(fieldName: string, reference: TextReference): void {
        const currentRefs = this.fieldTextReferencesSubject.value;
        const newRefs = new Map(currentRefs);
        newRefs.set(fieldName, reference);
        this.fieldTextReferencesSubject.next(newRefs);
    }

    /**
     * Get text reference for a specific field
     */
    public getFieldTextReference(fieldName: string): TextReference | undefined {
        return this.fieldTextReferencesSubject.value.get(fieldName);
    }

    /**
     * Get text reference for a specific enum field
     */
    public getEnumFieldTextReference(enumValue: AgreementMetaDataConstants): TextReference | undefined {
        const fieldName = AgreementMetaDataConstants[enumValue];
        return this.getFieldTextReference(fieldName);
    }

    /**
     * Check if a field has a text reference
     */
    public hasFieldTextReference(fieldName: string): boolean {
        return this.fieldTextReferencesSubject.value.has(fieldName);
    }

    /**
     * Check if an enum field has a text reference
     */
    public hasEnumFieldTextReference(enumValue: AgreementMetaDataConstants): boolean {
        const fieldName = AgreementMetaDataConstants[enumValue];
        return this.hasFieldTextReference(fieldName);
    }

    /**
     * Clear all text references
     */
    public clearTextReferences(): void {
        this.fieldTextReferencesSubject.next(new Map());
    }

    /**
     * Get all text references
     */
    public getAllTextReferences(): Map<string, TextReference> {
        return this.fieldTextReferencesSubject.value;
    }

    /**
     * Observable for field focus events (for PDF highlighting)
     */
    public get fieldFocused$() {
        return this.fieldFocusedSubject.asObservable();
    }

    /**
     * Notify that a field with PDF reference was focused
     */
    public notifyFieldFocused(fieldType: AgreementMetaDataConstants) {
        this.fieldFocusedSubject.next(fieldType);
    }
}
